// Agents paused on a plan usage limit, as their daemon says (cyborg:agent_usage_limit): one fact
// for the conversation, the header and the sidebar. A viewer hears only their own held messages.
import type { ModelChoice, UsageLimit } from "./usage-limit.js";

export interface HeldMark {
  promptId?: string;
  messageId?: string;
  heldUntil: number;
}

export interface UsageLimitFrame {
  workspaceId: string;
  cyboId: string;
  limit?: UsageLimit;
  held: HeldMark[];
}

// "Switch <agent> to X": the quiet line under the message it was held on, with Undo.
export interface Switched {
  after?: HeldMark; // the message it was held on: the line goes under it
  at?: number; // nothing held: the line goes where the transcript stood at the switch
  to: ModelChoice;
  from: { provider: string; model: string | null }; // null: the agent's model was the default
}

// Two per-viewer conveniences outlive a reload in this browser: the switcher's own line, per agent,
// until Undo or another switch (AgentStreamView shows it only while the agent still runs on that
// AI), and the one-off answer's "via <AI>" for each held message it answered (its client id and
// DM row id), the newest ANSWERED_KEEP of them.
const SWITCHED_KEY = "cyborg7-usage-limit-switched";
const ANSWERED_KEY = "cyborg7-usage-limit-answered";
const ANSWERED_KEEP = 200;

function loadRecord<T>(key: string): Record<string, T> {
  try {
    const parsed: unknown = JSON.parse(globalThis.localStorage?.getItem(key) ?? "{}");
    return parsed && typeof parsed === "object" ? (parsed as Record<string, T>) : {}; // cast: our own write
  } catch {
    // intentional: no storage (a private window) or a corrupt value is nothing remembered
    return {};
  }
}

function saveRecord(key: string, value: Record<string, unknown>): void {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // intentional: no storage (a private window, quota): it lasts until a reload
  }
}

class UsageLimitState {
  byCybo: Record<string, { limit?: UsageLimit; held: HeldMark[] }> = $state({});
  answered: Record<string, ModelChoice> = $state(loadRecord<ModelChoice>(ANSWERED_KEY));
  switched: Record<string, Switched> = $state(loadRecord<Switched>(SWITCHED_KEY));

  noteSwitch(cyboId: string, s: Switched | undefined): void {
    if (s) this.switched[cyboId] = s;
    else delete this.switched[cyboId];
    saveRecord(SWITCHED_KEY, this.switched);
  }

  noteAnswered(ids: ReadonlyArray<string | undefined>, choice: ModelChoice): void {
    for (const id of ids) if (id) this.answered[id] = choice;
    saveRecord(
      ANSWERED_KEY,
      Object.fromEntries(Object.entries(this.answered).slice(-ANSWERED_KEEP)),
    );
  }

  apply(frame: UsageLimitFrame): void {
    if (!frame.limit && frame.held.length === 0) delete this.byCybo[frame.cyboId];
    else this.byCybo[frame.cyboId] = { limit: frame.limit, held: frame.held };
  }

  /** The limit while the daemon says it lasts (never this clock: it may resend later); undefined: not paused. */
  limit(cyboId: string | undefined): UsageLimit | undefined {
    return cyboId ? this.byCybo[cyboId]?.limit : undefined;
  }

  held(cyboId: string | undefined): HeldMark[] {
    return (cyboId && this.byCybo[cyboId]?.held) || [];
  }

  heldMark(cyboId: string | undefined, ids: { promptId?: string; messageId?: string }) {
    return this.held(cyboId).find((h) => sameMessage(h, ids));
  }
}

// A transcript row and a held mark name one message by its client id, else its DM row id.
export function sameMessage(
  mark: { promptId?: string; messageId?: string } | undefined,
  ids: { promptId?: string; messageId?: string },
): boolean {
  if (!mark) return false;
  return (
    (!!ids.promptId && mark.promptId === ids.promptId) ||
    (!!ids.messageId && mark.messageId === ids.messageId)
  );
}

export const usageLimitState = new UsageLimitState();
